import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Input, InputNumber, Button } from "antd";
import { toast } from "react-toastify";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { AI_TIMEOUT, startAILoading } from "api/aiLoading";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import {
  Card,
  LabelMeta,
  LoadingState,
  EmptyState,
  theme,
  formatDateTime,
} from "../components/ui";

const SCORE_FIELDS = [
  { key: "technicalSkills", label: "Technical Skills", icon: "💻" },
  { key: "communication", label: "Communication", icon: "🗣️" },
  { key: "problemSolving", label: "Problem Solving", icon: "🧩" },
  { key: "experienceFit", label: "Experience Fit", icon: "📚" },
  { key: "teamFit", label: "Team Fit", icon: "🤝" },
  { key: "overallRating", label: "Overall Rating", icon: "⭐" },
];

const EMPTY_FORM = {
  technicalSkills: 0,
  communication: 0,
  problemSolving: 0,
  experienceFit: 0,
  teamFit: 0,
  overallRating: 0,
  strengths: "",
  concerns: "",
  keyObservations: "",
};

// A feedback record only exists once HR submits it (FeedbackAdded) and persists
// through the later pipeline stages. Before that — e.g. at InterviewCompleted —
// there is no record, so calling get-by-applicant errors on the backend. We use
// this set to skip that fetch for earlier statuses. Reject has its own view and
// never displays feedback, so it's intentionally excluded too.
const FEEDBACK_PRESENT_STATUSES = [
  JobApplicationStatusEnum.FeedbackAdded,
  JobApplicationStatusEnum.DocumentSend,
  JobApplicationStatusEnum.DocumentRecieved,
  JobApplicationStatusEnum.OnboardingDone,
  JobApplicationStatusEnum.Hired,
];

function FeedbackTab({
  jobApplicationId,
  applicantData,
  onFeedbackPosted,
  isPostingFilled = false,
}) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const status = applicantData?.status;
  const isRejected = status === JobApplicationStatusEnum.Reject;
  const feedbackShouldExist = FEEDBACK_PRESENT_STATUSES.includes(status);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        API.feedback.getByJobApplicantId(jobApplicationId)
      );
      setFeedback(response.data);
    } catch (error) {
      // Silent — backend may return non-404 (e.g. 500) when there's simply no
      // feedback yet. Treat any failure as "no feedback" and let the empty
      // state or the Post Feedback CTA render. Don't spam toast errors here.
      console.error("Error fetching feedback (treated as no-feedback):", error);
      setFeedback(null);
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (!jobApplicationId) return;
    // Only fetch when the pipeline status implies a feedback record exists.
    // Before HR submits feedback (e.g. InterviewCompleted) there is nothing to
    // fetch and get-by-applicant errors on the backend, so skip the call and
    // let the Post Feedback CTA / empty state render instead.
    if (!feedbackShouldExist) {
      setFeedback(null);
      setLoading(false);
      return;
    }
    fetchFeedback();
  }, [jobApplicationId, feedbackShouldExist, fetchFeedback]);

  const openPostFeedback = () => {
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    // Basic validation
    const numericFields = SCORE_FIELDS.map((f) => form[f.key]);
    if (numericFields.some((v) => v == null || v < 1 || v > 10)) {
      toast.warning("Please enter a score between 1 and 10 for all categories.");
      return;
    }
    let stopAILoading;
    try {
      setSubmitting(true);
      // Provide-feedback triggers AI re-evaluation on the backend, so bump
      // timeout and show rotating loading messages.
      stopAILoading = startAILoading();
      await axiosInstance.post(
        API.feedback.provideFeedback,
        {
          jobApplicantId: applicantData?.jobApplicantId || jobApplicationId,
          ...form,
        },
        { timeout: AI_TIMEOUT }
      );
      toast.success("Feedback submitted successfully.");
      setModalOpen(false);
      await fetchFeedback();
      if (onFeedbackPosted) onFeedbackPosted();
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.details ||
          "Failed to submit feedback. Please try again."
      );
    } finally {
      setSubmitting(false);
      if (stopAILoading) stopAILoading();
    }
  };

  // ---------- Rejected ----------
  if (isRejected) {
    return (
      <EmptyState
        icon="🚫"
        title="Candidate rejected"
        hint="No feedback actions are available for rejected applications."
      />
    );
  }

  if (loading) return <LoadingState label="Loading feedback..." />;

  // ---------- No feedback yet ----------
  if (!feedback) {
    // Show Post Feedback CTA if interviews are completed AND the posting
    // is still open. Once the posting is Filled, no further hiring-pipeline
    // actions are allowed (per Mudassir).
    if (status === JobApplicationStatusEnum.InterviewCompleted) {
      if (isPostingFilled) {
        return (
          <EmptyState
            icon="🔒"
            title="Feedback locked"
            hint="This job posting has been marked as Filled — no further hiring actions are allowed."
          />
        );
      }
      return (
        <>
          <div style={{ padding: "1.5rem" }}>
            <Card title="Post Interview Feedback" icon="📝">
              <p
                style={{
                  margin: "0 0 14px 0",
                  fontSize: "0.92rem",
                  color: theme.text,
                  lineHeight: 1.5,
                }}
              >
                All interview rounds are complete. Submit the candidate&apos;s
                final feedback so the application can move forward.
              </p>
              <button
                type="button"
                onClick={openPostFeedback}
                style={primaryBtn}
              >
                📝 Post Feedback
              </button>
            </Card>
          </div>
          <PostFeedbackModal
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            onSubmit={handleSubmit}
            submitting={submitting}
            form={form}
            setForm={setForm}
          />
        </>
      );
    }

    return (
      <EmptyState
        icon="📝"
        title="No feedback submitted yet"
        hint="Feedback can be posted once all interview rounds are completed."
      />
    );
  }

  // ---------- Feedback exists — display read-only ----------
  const numericScores = SCORE_FIELDS
    .map((f) => feedback[f.key])
    .filter((s) => typeof s === "number");
  const average =
    numericScores.length > 0
      ? numericScores.reduce((s, v) => s + v, 0) / numericScores.length
      : null;

  // Score card lives on a navy hero, so we deliberately move away from
  // brand green for the number itself. Gold for strong, amber for mid, red
  // for low — reads clearly against the blue without clashing.
  const avgColor =
    average == null
      ? "rgba(255,255,255,0.7)"
      : average >= 8
      ? "#fbbf24"   // amber-gold
      : average >= 5
      ? "#fb923c"   // warm orange
      : "#fca5a5";  // soft red

  return (
    <div style={{ padding: "1.5rem" }}>
      {/* Hero with average — muted teal palette: distinct from the brand-green
          candidate hero above and from the charcoal Career Path hero. */}
      <div
        style={{
          background: "linear-gradient(135deg, #1f3a3d 0%, #2c5559 100%)",
          borderRadius: "28px",
          padding: "20px 24px",
          marginBottom: "1.5rem",
          color: "white",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          boxShadow: "0 12px 22px rgba(31,58,61,0.18)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.85,
              fontWeight: 600,
            }}
          >
            Interview Feedback
          </div>
          <div
            style={{
              fontSize: "1.4rem",
              fontWeight: 600,
              marginTop: "4px",
            }}
          >
            📝 Evaluation Summary
          </div>
          {(feedback.lastModifiedOn || feedback.createdOn) && (
            <div style={{ fontSize: "0.8rem", opacity: 0.9, marginTop: "6px" }}>
              {/* Per Mudassir: use lastModifiedOn (actual submission time)
                  rather than createdOn (which defaults to interview-complete
                  time when the empty feedback record is seeded). */}
              Submitted: {formatDateTime(feedback.lastModifiedOn || feedback.createdOn)}
            </div>
          )}
        </div>
        <div
          style={{
            textAlign: "center",
            background: "rgba(255,255,255,0.12)",
            borderRadius: "24px",
            padding: "12px 24px",
            border: "1.5px solid rgba(255,255,255,0.25)",
          }}
        >
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.85,
            }}
          >
            Average Score
          </div>
          <div
            style={{
              fontSize: "2.4rem",
              fontWeight: 700,
              color: avgColor,
              lineHeight: 1.1,
              marginTop: "4px",
            }}
          >
            {average == null ? "—" : average.toFixed(1)}
            <span style={{ fontSize: "1rem", fontWeight: 500, opacity: 0.85 }}>
              /10
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <Card title="Evaluation Scores" icon="📊">
          <LabelMeta style={{ marginBottom: "10px" }}>1 – 10 scale</LabelMeta>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {SCORE_FIELDS.map((f) => (
              <ScoreRow
                key={f.key}
                icon={f.icon}
                label={f.label}
                value={feedback[f.key]}
              />
            ))}
          </div>
        </Card>

        <Card title="Interview Insights" icon="🔍">
          <InsightBlock
            label="💪 Strengths observed"
            value={feedback.strengths}
            accent="#2b7e3a"
          />
          <InsightBlock
            label="⚠️ Concerns / Areas for improvement"
            value={feedback.concerns}
            accent="#c26b2c"
          />
          <InsightBlock
            label="🧠 Key behavioral & technical observations"
            value={feedback.keyObservations}
            accent="#1677ff"
          />
        </Card>
      </div>
    </div>
  );
}

FeedbackTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  applicantData: PropTypes.object,
  onFeedbackPosted: PropTypes.func,
  isPostingFilled: PropTypes.bool,
};

// ============================================================

function PostFeedbackModal({ open, onCancel, onSubmit, submitting, form, setForm }) {
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  return (
    <Modal
      open={open}
      title="Post Interview Feedback"
      width={760}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          loading={submitting}
          style={{ background: "#0f5a2a", borderColor: "#0f5a2a" }}
        >
          Submit Feedback
        </Button>,
      ]}
    >
      <div
        style={{
          fontSize: "0.78rem",
          color: theme.textMuted,
          marginBottom: "12px",
        }}
      >
        Score each category from 1 to 10. All scores required.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "10px 14px",
          marginBottom: "16px",
        }}
      >
        {SCORE_FIELDS.map((f) => (
          <div key={f.key}>
            <label
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: theme.text,
                display: "block",
                marginBottom: "4px",
              }}
            >
              {f.icon} {f.label}
            </label>
            <InputNumber
              min={1}
              max={10}
              value={form[f.key] || null}
              onChange={(v) => update(f.key, v)}
              style={{ width: "100%" }}
              placeholder="1–10"
            />
          </div>
        ))}
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          💪 Strengths observed
        </label>
        <Input.TextArea
          rows={3}
          value={form.strengths}
          onChange={(e) => update("strengths", e.target.value)}
          placeholder="Strong technical background, etc."
        />
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          ⚠️ Concerns / areas for improvement
        </label>
        <Input.TextArea
          rows={3}
          value={form.concerns}
          onChange={(e) => update("concerns", e.target.value)}
          placeholder="Limited exposure to X, etc."
        />
      </div>

      <div>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          🧠 Key behavioral & technical observations
        </label>
        <Input.TextArea
          rows={3}
          value={form.keyObservations}
          onChange={(e) => update("keyObservations", e.target.value)}
          placeholder="Communicated well, problem-solving was structured, etc."
        />
      </div>
    </Modal>
  );
}
PostFeedbackModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  form: PropTypes.object,
  setForm: PropTypes.func,
};

function ScoreRow({ icon, label, value }) {
  const numeric = typeof value === "number";
  const color = !numeric
    ? theme.textMuted
    : value >= 8
    ? "#22c55e"
    : value >= 5
    ? "#f59e0b"
    : "#ef4444";
  const pct = numeric ? Math.min(100, Math.max(0, (value / 10) * 100)) : 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.9rem",
          marginBottom: "5px",
        }}
      >
        <span style={{ color: theme.text, fontWeight: 500 }}>
          <span style={{ marginRight: "8px" }}>{icon}</span>
          {label}
        </span>
        <span
          style={{
            fontWeight: 700,
            color,
            fontSize: "0.95rem",
            minWidth: "40px",
            textAlign: "right",
          }}
        >
          {numeric ? `${value}/10` : "N/A"}
        </span>
      </div>
      <div
        style={{
          background: "#e4eaf2",
          borderRadius: "20px",
          height: "6px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            background: color,
            height: "100%",
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}
ScoreRow.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.number,
};

function InsightBlock({ label, value, accent }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <LabelMeta>{label}</LabelMeta>
      <div
        style={{
          background: "#fafdfb",
          padding: "12px 14px",
          borderRadius: "18px",
          marginTop: "6px",
          fontSize: "0.88rem",
          lineHeight: 1.5,
          color: theme.text,
          borderLeft: `3px solid ${accent || "#7c9bc0"}`,
          whiteSpace: "pre-wrap",
          minHeight: "60px",
        }}
      >
        {value || (
          <span style={{ color: theme.textLabel, fontStyle: "italic" }}>
            No notes provided.
          </span>
        )}
      </div>
    </div>
  );
}
InsightBlock.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  accent: PropTypes.string,
};

const primaryBtn = {
  background: "#0f5a2a",
  color: "white",
  border: "none",
  padding: "10px 22px",
  borderRadius: "40px",
  fontSize: "0.88rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 2px 6px rgba(15,90,42,0.18)",
};

export default FeedbackTab;
